import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import apiUrl from "../../Apiurl";
import { BlockSkeleton } from "../../interfaccia/loading";
import "./magazzino.css";
import { RigaLetturaMagazzino, RigaAttesoMagazzino } from "./RigheMagazzino";
import { chiaveSessione, haCoda, salvaSessioneLocale, leggiSessioneLocale } from "./sessioniLocali";
import { pianificaSparo } from "./scanner";
import { GuidaScansione, AnteprimaArticolo } from "./GuidaScansione";

const nuovaChiave = () => window.crypto.randomUUID();
const errore = e => e.response?.data?.error || e.message || "Richiesta non riuscita";
const valida = l => ["VALIDA", "FUORI_ATTESI"].includes(l.esito) && !l.confermata;

function riepilogoSessione(sessione) {
  const attesi=sessione?.snapshot || [];
  const confermati=attesi.filter(u=>u.corrente?.coerente && (sessione.azione === "TROVATO" ? ["TROVATO","PRONTO"].includes(u.corrente.stato) : u.corrente.stato === "PRONTO")).length;
  const nonClassificati=sessione?.non_classificati || [];
  const totaleAttesi=attesi.length+nonClassificati.reduce((n,a)=>n+(Number(a.quantita)>0?Number(a.quantita):0),0);
  const percentuale=totaleAttesi?Math.floor(confermati*100/totaleAttesi):0;
  const provvisorie=sessione?.letture.filter(l=>valida(l)).length || 0;
  return {attesi,confermati,nonClassificati,totaleAttesi,percentuale,provvisorie};
}

export default function ControlloMagazzino({ utente }) {
  const { getAccessTokenSilently } = useAuth0();
  const [config, setConfig] = useState(null);
  const [tentativoConfig, setTentativoConfig] = useState(0);
  const [sessione, setSessione] = useState(null);
  const [recenti, setRecenti] = useState([]);
  const [magazzino, setMagazzino] = useState("");
  const [azione, setAzione] = useState("TROVATO");
  const [modalita, setModalita] = useState("DETTAGLIO");
  const [input, setInput] = useState("");
  const [errorePagina, setErrorePagina] = useState("");
  const [ultimo, setUltimo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pausa, setPausa] = useState(false);
  const [reteInPausa, setReteInPausa] = useState(false);
  const [queueView, setQueueView] = useState({ queue: [], pending: null });
  const [selezione, setSelezione] = useState({});
  const [fuoriAttesi, setFuoriAttesi] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [audio, setAudio] = useState(false);
  const [rettifica, setRettifica] = useState({id_articolo:"",id_unita:"",barcode:"",provenienza:"IMPORT_SITO",motivo:""});
  const [esitoRettifica, setEsitoRettifica] = useState("");
  const [esitiConferma, setEsitiConferma] = useState([]);
  const inputRef = useRef(null);
  const local = useRef({queue:[],pending:null});
  const running = useRef(false);
  const leggiController = useRef(null);
  const permesso = utente?.permessiAPI?.["print:logistica"] || utente?.permessiAPI?.["update:logistica"];
  const debug = Boolean(utente?.permessiAPI?.["read:debug"]);
  const puoRiconciliare = Boolean(utente?.permessiAPI?.["update:logistica"]);
  const api = useCallback(async (method, path, data, signal) => {
    const token = await getAccessTokenSilently();
    const response = await axios({method,url:`${apiUrl}/logistica/magazzino${path}`,signal,
      headers:{Authorization:`Bearer ${token}`},
      ...(method === "get" ? {params:{username:utente?.username,...data}} : {data:{username:utente?.username,...data}})});
    return response.data;
  },[getAccessTokenSilently,utente?.username]);

  useEffect(() => {
    if (!permesso || !utente?.username) return undefined;
    const controller = new AbortController();
    (async () => {
      try {
        const c = await api("get","/config",{},controller.signal);
        if (controller.signal.aborted) return;
        setConfig(c);setMagazzino(c.magazzini[0]?.id || "");
        if (c.disponibile) {
          const list=await api("get","/sessioni",{},controller.signal);
          if (!controller.signal.aborted) setRecenti(list);
        }
      } catch(e) {if (!controller.signal.aborted) setErrorePagina(errore(e));}
    })();
    return () => {controller.abort();leggiController.current?.abort();};
  },[api,permesso,utente?.username,tentativoConfig]);

  const carica = async (id, applica = true) => {
    leggiController.current?.abort();
    const controller=new AbortController();leggiController.current=controller;
    try {
      const s=await api("get",`/sessioni/${id}`,{},controller.signal);
      if (!controller.signal.aborted) {
        if (applica) {
          setSessione(s);
          if (s.stato && s.stato!=="APERTA") {setPausa(true);setErrorePagina("Sessione sostituita o chiusa: riprendi quella corrente.");}
        }
        return s;
      }
    } catch(e) {if (!controller.signal.aborted) throw e;}
  };
  const persisti = value => {
    // Se lo storage è pieno non si dichiara acquisita la lettura.
    const saved=JSON.parse(localStorage.getItem(chiaveSessione(utente.username,sessione.azione))||"null");
    if(saved && saved.sessione!==sessione.id) throw new Error("Una nuova sessione è stata aperta su questa postazione: ricarica la pagina.");
    salvaSessioneLocale(localStorage,utente.username,sessione,value);
    local.current=value;setQueueView({...value});
  };
  const riprendi = async id => {
    setBusy(true);setErrorePagina("");
    try {
      const s=await carica(id,false);
      if (!s) return;
      const saved=leggiSessioneLocale(localStorage,utente.username,s);
      salvaSessioneLocale(localStorage,utente.username,s,saved);
      local.current=saved;setQueueView({...local.current});setSessione(s);
      setPausa(Boolean(s.stato && s.stato!=="APERTA"));setReteInPausa(false);setUltimo(null);setSelezione({});
    } catch(e) {setErrorePagina(errore(e));} finally {setBusy(false);}
  };
  const apri = async () => {
    setBusy(true);setErrorePagina("");
    try {
      const precedente=JSON.parse(localStorage.getItem(chiaveSessione(utente.username,azione))||"null");
      const legacy=recenti.filter(r=>r.azione===azione).some(r=>haCoda(JSON.parse(localStorage.getItem(`MAG:${utente.username}:${r.id}`)||"null")));
      if (haCoda(precedente) || legacy) throw new Error("Riprendi la sessione precedente e completa le letture locali prima di aprirne una nuova.");
      const s=await api("post","/sessioni",{magazzino,azione,modalita});
      salvaSessioneLocale(localStorage,utente.username,s,{queue:[],pending:null});
      local.current={queue:[],pending:null};setQueueView({...local.current});
      setSessione(s);setRecenti(list=>[s,...list.filter(r=>r.azione!==s.azione)].slice(0,2));setPausa(false);setReteInPausa(false);setSelezione({});setUltimo(null);
    } catch(e) {setErrorePagina(errore(e));} finally {setBusy(false);}
  };
  const feedback = success => {
    if (!audio) return;
    try {
      const AudioContext=window.AudioContext || window.webkitAudioContext;
      const context=new AudioContext();const oscillator=context.createOscillator();
      const gain=context.createGain();gain.gain.value=0.08;oscillator.frequency.value=success?880:220;
      oscillator.connect(gain);gain.connect(context.destination);oscillator.start();
      oscillator.stop(context.currentTime+0.12);oscillator.onended=()=>context.close();
    } catch (_) { /* Il feedback visivo resta sempre disponibile. */ }
  };
  const inviaLettura = async (item, prodotto) => {
    const l=await api("post",`/sessioni/${sessione.id}/letture`,{
      client_id:item.id,sequenza:item.sequenza,metodo:"SCANSIONE",barcode:item.barcode,barcode_prodotto:prodotto || null,
    });
    setUltimo(l);feedback(l.esito === "VALIDA");
    if (sessione.modalita === "DETTAGLIO" && l.esito === "VALIDA") {
      const result=await api("post",`/sessioni/${sessione.id}/conferme`,{client_id:`ok_${item.id}`,letture:[l.id]});
      setEsitiConferma(result);
      if (!result.every(r=>["CONFERMATA","GIA_ATTESTATA","GIA_CONFERMATA"].includes(r.esito))) {setPausa(true);l.bloccaDettaglio=true;}
    } else if (sessione.modalita === "DETTAGLIO") setPausa(true);
    await carica(sessione.id);
    return l;
  };
  const lavora = async () => {
    if (running.current || pausa || reteInPausa || !sessione || !config?.scritture) return;
    running.current=true;
    try {
      while (local.current.queue.length) {
        const current=local.current.queue[0];
        const tipo=Number(sessione.doppio_controllo) ? await api("get","/identifica",{barcode:current.barcode}) : {matricola:false};
        const plan=pianificaSparo({pending:local.current.pending,current,doppioControllo:!!Number(sessione.doppio_controllo),matricola:tipo.matricola});
        const esito=plan.lettura?await inviaLettura(plan.lettura,plan.prodotto):null;
        persisti({queue:local.current.queue.slice(1),pending:plan.pending});
        if (sessione.modalita === "DETTAGLIO" && esito && (esito.esito!=="VALIDA" || esito.bloccaDettaglio)) break;
      }
    } catch(e) {setErrorePagina(`${errore(e)}. Le letture in coda sono conservate su questa postazione: riprova.`);setReteInPausa(true);if (sessione.modalita === "DETTAGLIO") setPausa(true);}
    finally {running.current=false;}
  };
  useEffect(() => { lavora(); /* La coda è serializzata tramite il ref running. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[queueView.queue.length,pausa,reteInPausa,sessione?.id]);
  useEffect(() => { if (sessione?.id && !pausa) inputRef.current?.focus(); },[sessione?.id,pausa]);

  useEffect(() => {
    const online = () => {setReteInPausa(false);};
    window.addEventListener("online",online);
    return () => window.removeEventListener("online",online);
  },[]);

  const chiudiCoppia = async () => {
    if (!local.current.pending || running.current) return;
    setBusy(true);
    try {await inviaLettura(local.current.pending);persisti({...local.current,pending:null});}
    catch(e){setErrorePagina(errore(e));} finally {setBusy(false);}
  };
  const acquisisci = e => {
    e.preventDefault();if (!input.trim() || !sessione || pausa) return;
    try {
      const item={id:nuovaChiave(),barcode:input.trim(),sequenza:Date.now()%4294967295};
      persisti({...local.current,queue:[...local.current.queue,item]});setInput("");setErrorePagina("");
    } catch(e) {setErrorePagina(`Lettura non acquisita: ${errore(e)}`);}
  };
  const conferma = async () => {
    setBusy(true);setErrorePagina("");
    try {
      const ids=sessione.letture.filter(l=>valida(l)&&selezione[l.id]).map(l=>l.id);
      const result=await api("post",`/sessioni/${sessione.id}/conferme`,{client_id:nuovaChiave(),letture:ids,conferma_fuori_attesi:fuoriAttesi});
      setEsitiConferma(result);await carica(sessione.id);
    } catch(e) {setErrorePagina(errore(e));} finally {setBusy(false);}
  };
  const manuale = async (u,stato) => {
    setBusy(true);setErrorePagina("");
    try {
      const l=await api("post",`/sessioni/${sessione.id}/letture`,{client_id:nuovaChiave(),sequenza:Date.now()%4294967295,
        metodo:"MANUALE",id_unita:u.id,azione:stato,motivo});
      setUltimo(l);await carica(sessione.id);
    } catch(e) {setErrorePagina(errore(e));} finally {setBusy(false);}
  };
  const riconcilia = async tipo => {
    setBusy(true);setErrorePagina("");setEsitoRettifica("");
    try {
      await api("post",tipo === "origine" ? "/classificazione" : "/associazioni",{
        ...rettifica,id_articolo:Number(rettifica.id_articolo),id_unita:Number(rettifica.id_unita),magazzino:sessione?.magazzino || magazzino,
      });
      setEsitoRettifica("Operazione registrata. Per includere nuove unità negli attesi apri una nuova sessione.");
    } catch(e) {setErrorePagina(errore(e));} finally {setBusy(false);}
  };
  if (!permesso) return <p className="pa4">È richiesto il permesso print:logistica oppure update:logistica.</p>;
  if (!utente?.username) return <p className="pa4">Profilo Domino incompleto: ricaricare l’accesso per recuperare username.</p>;
  const {attesi,confermati,nonClassificati,totaleAttesi,percentuale,provvisorie}=riepilogoSessione(sessione);
  return <main className="mag-workspace pa3 pa4-ns">
    {errorePagina && <div role="alert" className="mag-notice ba bordoColAlt pa3">{errorePagina}{!config && <button className="tastoBianco db mt2" onClick={()=>{setErrorePagina("");setTentativoConfig(n=>n+1);}}>Riprova caricamento</button>}</div>}
    {debug && config?.disponibile && <details className="mb3"><summary>Riconciliazione logistica</summary>
      <div className="pa3 ba bordoColAlt"><p>Magazzino: {sessione?.partenza_da || config.magazzini.find(m=>m.id===magazzino)?.nome || "Selezionare un magazzino"}. Classifica le righe pregresse soltanto dopo averne verificato la provenienza.</p>
        <div className="mag-controls"><label>ID articolo<input type="number" value={rettifica.id_articolo} onChange={e=>setRettifica({...rettifica,id_articolo:e.target.value})}/></label>
          <label>Provenienza<select value={rettifica.provenienza} onChange={e=>setRettifica({...rettifica,provenienza:e.target.value})}><option value="IMPORT_SITO">Import sito</option><option value="CUSTOM">Custom / fuori circuito</option></select></label>
          <button className="tastoBianco" disabled={!puoRiconciliare || busy || !config.scritture || !rettifica.id_articolo || rettifica.motivo.length<5} onClick={()=>riconcilia("origine")}>Registra origine verificata</button></div>
        <div className="mag-controls mt3"><label>ID pezzo<input type="number" value={rettifica.id_unita} onChange={e=>setRettifica({...rettifica,id_unita:e.target.value})}/></label>
          <label>Matricola<input value={rettifica.barcode} onChange={e=>setRettifica({...rettifica,barcode:e.target.value})}/></label>
          <button className="tastoBianco" disabled={!puoRiconciliare || busy || !config.scritture || !rettifica.id_unita || !rettifica.barcode || rettifica.motivo.length<5} onClick={()=>riconcilia("associazione")}>Associa al pezzo verificato</button></div>
        <label className="db mt3">Motivazione<input className="ml2" maxLength={500} value={rettifica.motivo} onChange={e=>setRettifica({...rettifica,motivo:e.target.value})}/></label>
        {esitoRettifica && <p role="status">{esitoRettifica}</p>}
      </div></details>}
    {!config && !errorePagina && <div role="status" aria-label="Caricamento magazzini e impostazioni"><p>Caricamento magazzini e impostazioni…</p><BlockSkeleton height={150} /></div>}
    {config && !config.disponibile && <p>Modulo in preparazione: lo schema magazzino non è ancora installato.</p>}
    {config?.disponibile && !sessione && <section className="mag-setup">
      <h2>Spedizioni attese del giorno</h2><p>Il bacino comprende anche gli ordini arretrati negli stati previsti per l’azione.</p>
      <div className="mag-controls">
        <label>Magazzino<select value={magazzino} onChange={e=>setMagazzino(e.target.value)}>{config.magazzini.map(m=><option key={m.id} value={m.id}>{m.nome} — {m.descPartenzaDa}</option>)}</select></label>
        <label>Azione<select value={azione} onChange={e=>setAzione(e.target.value)}><option>TROVATO</option><option>PRONTO</option></select></label>
        <label>Modalità<select value={modalita} onChange={e=>setModalita(e.target.value)}><option value="DETTAGLIO">Al dettaglio</option><option value="MASSIVA">Massiva alla cieca</option></select></label>
        <button className="sfondoColScuro" disabled={busy || !magazzino || !config.scritture} onClick={apri}>Apri sessione</button>
      </div>
      {!config.magazzini.length && <p>Nessun magazzino assegnato al tuo username.</p>}
      <p>{azione==="TROVATO" && config.doppioControlloTrovato?"Due spari: matricola prenotazione, poi barcode prodotto.":"Spara la matricola della prenotazione per ciascun pezzo."}</p>
      {recenti.some(r=>r.azione===azione) && <p>Aprendo una nuova sessione {azione}, la precedente verrà chiusa. Le letture restano nello storico: conferma quelle verificate prima di sostituire la sessione.</p>}
      {recenti.length>0 && <><h3>Riprendi una sessione</h3>{recenti.map(s=><button className="tastoBianco mr2 mb2" disabled={busy} key={s.id} onClick={()=>riprendi(s.id)}>{s.magazzino} · {s.azione} · {s.modalita} · {String(s.giorno).slice(0,10)}</button>)}</>}
    </section>}
    {sessione && <div className="mag-session-layout">
      <div className="mag-session-operazioni">
      <section className="mag-acquisizione sfondoColChiaro pa3">
        <div className="mag-header"><h2>{sessione.partenza_da} · {sessione.azione}</h2><span>{sessione.modalita} · {String(sessione.giorno).slice(0,10)}</span></div>
        <GuidaScansione prodotto={!!queueView.pending} sospesa={pausa || reteInPausa} inCoda={queueView.queue.length > 0}/>
        <label htmlFor="mag-scan" className="db b mb2">{queueView.pending?`Barcode prodotto per ${queueView.pending.barcode}`:"Matricola prenotazione"}</label>
        <form onSubmit={acquisisci} className="mag-scan-form"><input id="mag-scan" ref={inputRef} value={input} autoComplete="off" spellCheck={false}
          disabled={!config.scritture || pausa} onChange={e=>setInput(e.target.value)} placeholder="Spara il barcode e premi Invio" />
          <button className="sfondoColScuro" disabled={!config.scritture || pausa}>Acquisisci</button></form>
        {queueView.pending && <AnteprimaArticolo key={`${sessione.id}:${queueView.pending.id}`} api={api} sessioneId={sessione.id} barcode={queueView.pending.barcode}/>}
        <div className="mag-controls mt2"><span>{queueView.queue.length} in coda locale · {provvisorie} da confermare</span>
          <button type="button" className="tastoBianco" onClick={()=>inputRef.current?.focus()}>Riprendi focus</button>
          <button type="button" className="tastoBianco" disabled={!queueView.pending || busy || queueView.queue.length>0} onClick={chiudiCoppia}>Registra coppia incompleta</button>
          <label><input type="checkbox" checked={audio} onChange={e=>setAudio(e.target.checked)}/> Audio aggiuntivo</label>
          {reteInPausa && <><span role="status">Invio sospeso · {sessione.modalita === "MASSIVA" ? "puoi continuare a sparare: la coda resta su questa postazione" : "letture conservate localmente"}</span>
            <button className="tastoBianco" onClick={()=>{setReteInPausa(false);setPausa(false);setErrorePagina("");}}>Riprova invio coda</button></>}
          {pausa && !reteInPausa && <button className="sfondoColScuro" onClick={()=>{setPausa(false);setErrorePagina("");}}>Ho verificato, continua</button>}
        </div>
        {ultimo && <div aria-live="polite" className="mag-result mt3"><strong>{ultimo.esito.replaceAll("_"," ")}</strong> · {ultimo.barcode || "Attestazione manuale"}<br/>{ultimo.dettaglio?.messaggio || `${ultimo.dettaglio?.fonte} ${ultimo.dettaglio?.ordine} · ${ultimo.dettaglio?.descrizione}`}</div>}
      </section>
      <section className="mag-progress"><div className="mag-header"><h2>{confermati} / {totaleAttesi} pezzi confermati</h2><strong>{percentuale}%</strong></div>
        <progress value={confermati} max={totaleAttesi || 1} aria-label="Pezzi confermati sul bacino atteso" />
        <p className="f6">Bacino fissato all’apertura · {new Set(attesi.map(u=>u.id_spedizione)).size} spedizioni. Le letture provvisorie non aumentano l’avanzamento.</p>
        <button className="tastoBianco" disabled={busy || queueView.queue.length>0 || !!queueView.pending} onClick={()=>{setSessione(null);setEsitiConferma([]);}}>Cambia sessione / aggiorna bacino</button>
      </section>
      <section className="mag-review"><h2>Letture e anomalie</h2><p>Seleziona le letture verificate e confermale. I tentativi restano nello storico della sessione.</p>
        <div className="mag-controls"><button className="tastoBianco" onClick={()=>setSelezione(Object.fromEntries(sessione.letture.filter(l=>valida(l)&&l.esito==="VALIDA").slice(0,500).map(l=>[l.id,true])))}>Seleziona valide (max 500)</button>
          <label><input type="checkbox" checked={fuoriAttesi} onChange={e=>setFuoriAttesi(e.target.checked)}/> Ho verificato le letture fuori attesi</label>
          <button className="sfondoColScuro" disabled={busy || !config.scritture || !sessione.letture.some(l=>valida(l)&&selezione[l.id]) || sessione.letture.filter(l=>valida(l)&&selezione[l.id]).length>500} onClick={conferma}>Conferma selezionate</button></div>
        <div className="mag-table-wrap"><table><thead><tr><th>Conferma</th><th>Matricola / pezzo</th><th>Articolo</th><th>Ordine / destinatario</th><th>Esito</th></tr></thead>
          <tbody>{sessione.letture.slice().reverse().map(l=><RigaLetturaMagazzino key={l.id} lettura={l} selezionata={!!selezione[l.id]}
            onSeleziona={checked=>setSelezione(previous=>({...previous,[l.id]:checked}))}/>)}</tbody></table></div>
        {esitiConferma.length>0 && <ul aria-live="polite">{esitiConferma.map(r=><li key={r.id}>Lettura {r.id}: {r.esito} {r.messaggio}</li>)}</ul>}
      </section>
      </div>
      <section className="mag-attesi" aria-labelledby="mag-attesi-title"><h2 id="mag-attesi-title">Pezzi attesi</h2>
        {debug && nonClassificati.length>0 && <div role="alert" className="ba bordoColAlt pa3 mb3"><strong>{nonClassificati.length} righe ancora da classificare</strong>
          <p>Il bacino richiede una verifica logistica. Dopo la classificazione, aprire una nuova sessione per aggiornare i pezzi attesi.</p>
          <ul>{nonClassificati.map(a=><li key={a.id_articolo}>{a.fonte} {a.N_rif_PrestaShop} · articolo {a.id_articolo} · {a.descrizione} · quantità {a.quantita ?? "non valida"}</li>)}</ul></div>}<label className="db mb3">Motivo delle attestazioni manuali<input className="ml2" value={motivo} onChange={e=>setMotivo(e.target.value)} placeholder="Es. articolo custom verificato" maxLength={500}/></label>
        <div className="mag-table-wrap"><table><thead><tr><th>Ordine / prenotazione</th><th>Articolo</th><th>Stato confermato</th><th>Fuori circuito</th></tr></thead>
          <tbody>{attesi.map(u=><RigaAttesoMagazzino key={u.presentazione?.barcode_matricola || u.id} unita={u}
            letture={sessione.letture} pending={queueView.pending} azione={sessione.azione}
            manualeDisabilitato={busy || !config.scritture || motivo.trim().length<3} onManuale={stato=>manuale(u,stato)}/>)}</tbody></table></div>
      </section>
    </div>}
  </main>;
}
